import React, { useState, ComponentType, useEffect } from 'react';

import { withFormik, FormikErrors } from 'formik';
import Utils from 'utilities/helper.util';
import {
  InitialEmailFormProps,
  LegacyEmailConfigurationType,
  DraftChannelDataType,
  initialEmailData,
  CHANNELS
} from 'types';
import { useDispatch, useSelector } from 'react-redux';
import {
  activateReferralChannelsStart,
  draftReferralChannelsStart,
  selectDraftChannels
} from 'redux/channels';
import { currentUser } from 'redux/session/selectors';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import FallbackChannelForm from './fallbackChannel.form';
dayjs.extend(utc);

export const FallbackChannelFormComponent: React.FC<any> = ({
  advancedOptions,
  copiedActiveValue,
  clearCopiedActiveValue,
  copiedAdvanceFeaturesValue
}) => {
  const dispatch = useDispatch();
  const [organisationId, setOrganisationId] = useState<any>();
  const [channelId, setChannelId] = useState<any>();
  const [emailFormValues, setEmailFormValues] = useState<InitialEmailFormProps>(
    initialEmailData
  );
  const user: any = useSelector(currentUser);
  useEffect(() => {
    setOrganisationId(user?.organisationId || user?.id);
  }, [user]);

  useEffect(() => {
    if (copiedActiveValue) {
      const values = JSON.parse(copiedActiveValue);
      values?.configurationType === CHANNELS.FALLBACK &&
        setEmailFormValues({
          ...initialEmailData,
          ...values
        });
    }
  }, [copiedActiveValue]);

  useEffect(() => {
    if (copiedAdvanceFeaturesValue) {
      const values = JSON.parse(copiedAdvanceFeaturesValue);
      advancedOptions.current = {
        maxEnquiryArea: values.maxEnquiryArea
          ? values.maxEnquiryArea / 1e6
          : undefined,
        splitEnquiries: values.maxEnquiryArea ? 'split' : undefined,
        referralSummaryFrequency: values.referralSummaryFrequency,
        referralSummaryEmail: values.referralSummaryEmail,
        isExpandPanel: true
      };
    }
  }, [copiedAdvanceFeaturesValue, advancedOptions]);

  useEffect(() => {
    clearCopiedActiveValue();
  }, [emailFormValues, clearCopiedActiveValue]);

  const draftChannel: any = useSelector(selectDraftChannels);
  useEffect(() => {
    if (draftChannel && draftChannel.configurationType === CHANNELS.FALLBACK) {
      const { id, name, LegacyEmailConfiguration } = draftChannel;
      setChannelId(id);
      const defalutValues: InitialEmailFormProps = {
        name: name || '',
        channelType: 'email',
        referralSummaryEmail: LegacyEmailConfiguration?.emailNormalEnquiries,
        isGifRequired: LegacyEmailConfiguration?.isGifRequired,
        isXmlRequired: LegacyEmailConfiguration?.isXmlRequired,
        isGmlRequired: LegacyEmailConfiguration?.isGmlRequired,
        isPdfRequired: LegacyEmailConfiguration?.isPdfRequired,
        gmlVersion: LegacyEmailConfiguration?.gmlVersion || '',
        gmlProjection: LegacyEmailConfiguration?.gmlProjection || '',
        requireAttachment: undefined,
        isPrimaryContentRequired: false
      };
      setEmailFormValues(defalutValues);
    }
  }, [draftChannel]);

  // Email Channel Sandbox Component
  const FormikComponent: ComponentType<any> = withFormik<
    DraftChannelDataType,
    InitialEmailFormProps
  >({
    mapPropsToValues: () => ({
      name: emailFormValues?.name || initialEmailData.name,
      channelType:
        emailFormValues?.channelType || initialEmailData?.channelType,
      referralSummaryEmail:
        emailFormValues?.referralSummaryEmail ||
        initialEmailData?.referralSummaryEmail,
      isGifRequired:
        emailFormValues?.isGifRequired || initialEmailData?.isGifRequired,
      isXmlRequired:
        emailFormValues?.isXmlRequired || initialEmailData?.isXmlRequired,
      isGmlRequired:
        emailFormValues?.isGmlRequired || initialEmailData?.isGmlRequired,
      gmlVersion: emailFormValues?.gmlVersion || initialEmailData?.gmlVersion,
      gmlProjection:
        emailFormValues?.gmlProjection || initialEmailData?.gmlProjection,
      isPdfRequired:
        emailFormValues?.isPdfRequired || initialEmailData?.isPdfRequired,
      isPrimaryContentRequired:
        emailFormValues?.isPrimaryContentRequired ||
        initialEmailData?.isPrimaryContentRequired
    }),
    mapPropsToTouched: () => {
      if (copiedActiveValue) {
        const values = JSON.parse(copiedActiveValue);
        if (values?.configurationType === CHANNELS.FALLBACK) {
          return values;
        }
      }

      return {};
    },
    // Custom sync validation
    validate: (values: InitialEmailFormProps) => {
      const errors: FormikErrors<InitialEmailFormProps> = {};
      const isRequiredAttachment = !(
        values.isGifRequired ||
        values.isGmlRequired ||
        values.isXmlRequired ||
        values.isPdfRequired
      );
      if (!values.referralSummaryEmail) {
        errors.referralSummaryEmail = 'Referral email address is required';
      } else {
        const emails = values.referralSummaryEmail.split(',');
        const invalidEmalis = emails.map((email: string) =>
          Utils.isInValidEmail(email)
        );
        if (invalidEmalis.includes(true)) {
          errors.referralSummaryEmail = 'Invalid Referral email address';
        }
      }
      if (isRequiredAttachment) {
        errors.requireAttachment = 'Require attachment is required';
      }
      if (values.isGmlRequired) {
        if (!values.gmlProjection) {
          errors.gmlProjection = 'GML Projection is required';
        }
      }
      return errors;
    },
    handleSubmit: (values: InitialEmailFormProps, { setSubmitting }) => {
      setSubmitting(true);
      const packReferralChannelPayload = (values: InitialEmailFormProps) => {
        setEmailFormValues(values);
        const {
          name,
          configurationType,
          referralSummaryEmail,
          isGifRequired,
          isXmlRequired,
          isGmlRequired,
          isPdfRequired
        } = values;

        const gmlProjection = isGmlRequired ? values.gmlProjection : undefined;
        const gmlVersion = isGmlRequired ? values.gmlVersion : undefined;
        const LegacyEmailConfiguration: LegacyEmailConfigurationType = {
          isGifRequired,
          isXmlRequired,
          isGmlRequired,
          gmlVersion,
          gmlProjection,
          emailNormalEnquiries: referralSummaryEmail,
          emailEmergencyEnquiries: referralSummaryEmail,
          emailLargeScaleEnquiries: referralSummaryEmail,
          isPdfRequired
        };

        const payload: DraftChannelDataType = {
          name,
          configurationType: configurationType || 'FALLBACK',
          LegacyEmailConfiguration
        };

        if (advancedOptions?.current) {
          payload.maxEnquiryArea =
            advancedOptions.current?.maxEnquiryArea &&
            advancedOptions.current?.splitEnquiries
              ? advancedOptions.current.maxEnquiryArea * 1e6
              : undefined;
          payload.referralSummaryFrequency =
            advancedOptions.current?.referralSummaryFrequency;
          payload.referralSummaryEmail =
            advancedOptions.current?.referralSummaryEmail &&
            advancedOptions.current?.referralSummaryFrequency !== 'NEVER'
              ? advancedOptions.current.referralSummaryEmail
              : undefined;
        }
        return payload;
      };
      if (values.actionType === 'make-active') {
        const activeChannelPayload: DraftChannelDataType = packReferralChannelPayload(
          values
        );
        const packedActivePayload: any = {
          ...activeChannelPayload,
          status: 'LIVE'
        };
        if (organisationId && channelId) {
          dispatch(
            activateReferralChannelsStart({
              organisationId,
              channelId,
              requestBody: packedActivePayload
            })
          );
        }
      }
      if (values.actionType === 'save-draft' && organisationId) {
        const name = dayjs(new Date()).utc().local().format().toString(); //TODO
        const draftPayload: DraftChannelDataType = packReferralChannelPayload(
          values
        );
        const packedDraftPayload: any = {
          ...draftPayload,
          name,
          status: 'DRAFT'
        };
        if (draftChannel?.LegacyEmailConfiguration !== null && channelId) {
          dispatch(
            activateReferralChannelsStart({
              organisationId,
              channelId,
              requestBody: packedDraftPayload
            })
          );
        } else {
          dispatch(
            draftReferralChannelsStart({
              organisationId,
              requestBody: packedDraftPayload
            })
          );
        }
      }
      setSubmitting(false);
    }
  })(FallbackChannelForm);

  return <FormikComponent advancedOptions={advancedOptions} />;
};
